/**
 * logoUtils.js
 * 
 * Unified utilities for parsing and resolving institution/college logos 
 * from various backend formats (raw strings, PostgreSQL array literals, JSON arrays).
 */

/**
 * Parses a raw logo string from the backend into a valid URL.
 * Handles:
 * - Absolute URLs (http...)
 * - PostgreSQL array literals (e.g., "{url1,url2}")
 * - JSON arrays (e.g., '["url1", "url2"]')
 * 
 * @param {string|any} raw - The raw logo value from the API
 * @returns {string|null} - The resolved URL or null
 */
export const parseLogoUrl = (raw) => {
  if (!raw || typeof raw !== 'string') {
    if (Array.isArray(raw) && raw.length > 0) return raw[0];
    return null;
  }

  const s = raw.trim();
  if (!s) return null;

  // 1. Simple absolute URL
  if (s.startsWith('http')) {
    return s;
  }

  // 2. PostgreSQL array literal: {url1,url2}
  if (s.startsWith('{') && s.endsWith('}')) {
    try {
      const parts = s.slice(1, -1).split(',');
      const first = parts[0]?.trim().replace(/^"|"$/g, '');
      return first && first.startsWith('http') ? first : null;
    } catch (e) {
      return null;
    }
  }

  // 3. JSON array: ["url1", "url2"]
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      const first = Array.isArray(parsed) ? parsed[0] : null;
      return typeof first === 'string' && first.startsWith('http') ? first : null;
    } catch (e) {
      return null;
    }
  }

  // 4. Fallback for relative paths if any (though currently not supported for serving)
  if (s.startsWith('/')) {
    return s;
  }

  return null;
};

/**
 * Returns a fallback logo if the primary logo is invalid.
 */
export const getFallbackLogo = () => '/logo192.png';
